import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, errText, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePlayer } from "../context/PlayerContext";
import { Avatar, CreatorName, formatNum, Loading, Empty, VScore } from "../components/common";
import { PostCard } from "../components/PostCard";
import { ProfileMusic } from "../components/musicui";
import { VideoCard } from "./TV";
import { TipModal } from "../components/TipModal";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { UserPlus, Check, MessageSquare, Coins, MapPin, Trophy, Star, Rocket, Flame, Link2, Share2, QrCode, Copy, X, Flag, ChevronDown, Instagram, Sparkles, ShieldCheck } from "lucide-react";
import { StoryShareModal } from "../components/StoryShareModal";
import { CitizenCard } from "../components/CitizenCard";
import { PUBLIC_WEB_URL } from "../lib/config";
import { QRCodeCanvas } from "qrcode.react";
import { ReportImpersonationModal } from "../components/ReportImpersonationModal";
import { ProfileDashboard } from "../components/ProfileDashboard";
import { ProfileBusiness } from "../components/ProfileBusiness";
import { FramedAvatar, BadgePill, bgStyle } from "../components/storeui";
import { ProductCard } from "../components/marketui";
import { ProductDetail } from "./Marketplace";
import { ShareWinModal } from "../components/ShareWinModal";
import { LayoutDashboard } from "lucide-react";
import { toast } from "sonner";

const ICONS = { plus: Star, chart: Trophy, trophy: Trophy, rising: Rocket, streams: Flame };

export default function Profile() {
  const params = useParams();
  const raw = params.handle ?? params.username ?? "";
  const username = raw.replace(/^@/, "");
  const { user: me } = useAuth();
  const nav = useNavigate();
  const player = usePlayer();
  const [u, setU] = useState(null);
  const [following, setFollowing] = useState(false);
  const [tab, setTab] = useState("posts");
  const [content, setContent] = useState({});
  const [tipOpen, setTipOpen] = useState(false);
  const [winCard, setWinCard] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [crews, setCrews] = useState([]);
  const [tabMenuOpen, setTabMenuOpen] = useState(false);
  const [storyOpen, setStoryOpen] = useState(false);
  const [shopDetail, setShopDetail] = useState(null);
  const storyOptions = u ? [{ type: "profile", label: "Profile" },
    ...((u.award_wins || []).slice().sort((a, b) => b.year - a.year).map((a) => ({ type: "win", label: `${a.year} ${a.category}`, title: `${a.category} Winner`, subtitle: `VirtuAwards ${a.year}` })))] : [];

  useEffect(() => { setU(null); setContent({}); setTab("posts"); setCrews([]); api.get(`/users/${username}`).then(({ data }) => { setU(data); setFollowing(data.is_following); }).catch(() => setU(false)); }, [username]);
  useEffect(() => { if (u?.id) api.get(`/crews/user/${u.id}`).then(({ data }) => setCrews(data)).catch(() => {}); }, [u]);
  useEffect(() => { if (u?.id && me?.id === u.id) setTab("dashboard"); }, [u]); // eslint-disable-line
  useEffect(() => {
    if (!u) return;
    const win = new URLSearchParams(window.location.search).get("win");
    if (win && win.startsWith("followers_")) {
      const n = Number(win.split("_")[1]);
      const tier = n >= 100000 ? "diamond" : n >= 10000 ? "gold" : "silver";
      setWinCard({ title: `${n.toLocaleString()} Followers`, subtitle: "Milestone on VirtuStars", tier });
    }
  }, [u]); // eslint-disable-line

  const needAuth = () => { toast("Join VirtuStars to do that"); nav("/register"); };
  const toggleFollow = async () => {
    if (!me) return needAuth();
    try { await (following ? api.delete(`/follow/${u.id}`) : api.post(`/follow/${u.id}`)); setFollowing(!following); setU((x) => ({ ...x, follower_count: x.follower_count + (following ? -1 : 1) })); }
    catch (e) { toast.error(errText(e)); }
  };
  const message = async () => { if (!me) return needAuth(); try { const { data } = await api.post("/conversations", { user_id: u.id }); nav("/messages", { state: { conv: data.id } }); } catch { toast.error("Error"); } };
  const openTip = () => { if (!me) return needAuth(); setTipOpen(true); };
  const primaryCrew = crews.find((c) => c.is_primary) || crews[0];

  // Canonical URL redirect (case-insensitive / history alias)
  useEffect(() => {
    if (u && u.username && raw !== u.username) {
      nav(`/${u.username}${window.location.search}`, { replace: true });
    }
  }, [u]); // eslint-disable-line

  useEffect(() => {
    if (!u) return;
    if (tab === "posts" && !content.posts) api.get(`/users/${u.id}/posts`).then(({ data }) => setContent((c) => ({ ...c, posts: data })));
    if (tab === "music" && !content.music) api.get(`/music/artist/${u.id}`).then(({ data }) => setContent((c) => ({ ...c, music: data.tracks, musicMeta: data })));
    if (tab === "tv" && !content.tv) api.get("/tv/videos").then(({ data }) => setContent((c) => ({ ...c, tv: data.filter((v) => v.channel_id === u.id) })));
    if (tab === "reels" && !content.reels) api.get("/reels").then(({ data }) => setContent((c) => ({ ...c, reels: data.filter((r) => r.author_id === u.id) })));
    if (tab === "shop" && !content.shop) api.get(`/marketplace/store/${u.username}`).then(({ data }) => setContent((c) => ({ ...c, shop: data }))).catch(() => setContent((c) => ({ ...c, shop: { products: [] } })));
  }, [tab, u]); // eslint-disable-line

  if (u === null) return <Loading />;
  if (u === false) return <Empty title="Creator not found" />;
  const isSelf = me?.id === u.id;
  const cz = u.customization || {};

  return (
    <div className="pb-5">
      <div className="relative h-40 md:h-56" style={bgStyle(cz.background) || { background: "linear-gradient(to bottom right,#131826,#000)" }} data-testid="profile-custom-bg">
        {u.banner_url && <img src={mediaUrl(u.banner_url)} className="w-full h-full object-cover" alt="" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#030303] to-transparent" />
      </div>
      <div className="px-4 md:px-6 -mt-12 relative">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <FramedAvatar src={u.avatar_url} name={u.display_name} size={96} frame={cz.frame} animation={cz.animation} />
          <div className="flex gap-2 pb-1">
            {isSelf ? <>
              <Button onClick={() => setStoryOpen(true)} className="rounded-full btn-blue" data-testid="profile-share-btn"><Share2 size={15} className="mr-1" />Share</Button>
              <Button onClick={() => nav("/settings")} variant="outline" className="rounded-full border-white/15 bg-white/5" data-testid="edit-profile-btn">Edit Profile</Button>
            </> : (
              <>
                <Button onClick={toggleFollow} className={`rounded-full ${following ? "btn-gold text-black" : "btn-blue"}`} data-testid="profile-follow-btn">{following ? <><Check size={15} className="mr-1" />Following</> : <><UserPlus size={15} className="mr-1" />Follow</>}</Button>
                <Button onClick={message} variant="outline" className="rounded-full border-white/15 bg-white/5" data-testid="profile-message-btn"><MessageSquare size={15} /></Button>
                <Button onClick={openTip} className="btn-gold rounded-full" data-testid="profile-tip-btn"><Coins size={15} className="mr-1" />Tip</Button>
                <Button onClick={() => setStoryOpen(true)} variant="outline" className="rounded-full border-white/15 bg-white/5" data-testid="profile-share-btn"><Share2 size={15} /></Button>
                <Button onClick={() => me ? setReportOpen(true) : nav("/register")} variant="outline" className="rounded-full border-white/15 bg-white/5 text-muted-foreground" title="Report impersonation" data-testid="profile-report-btn"><Flag size={15} /></Button>
              </>
            )}
          </div>
        </div>
        <div className="mt-3">
          <h1 className={`font-display font-bold text-2xl flex items-center gap-2 ${cz.animation?.config?.target === "name" ? "vs-anim-shimmer text-gold" : ""}`}><CreatorName user={u} /></h1>
          <div className="text-muted-foreground text-sm">@{u.username}</div>
          {u.bio && <p className="text-sm mt-2 max-w-xl">{u.bio}</p>}
          {cz.badges?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="profile-store-badges">
              {cz.badges.map((b) => <BadgePill key={b.id} badge={b} testid={`profile-badge-${b.id}`} />)}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {u.virtucity_citizen && <span className="text-xs rounded-full px-2.5 py-1 bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 flex items-center gap-1 font-semibold" data-testid="profile-virtucity-badge"><ShieldCheck size={12} />VirtuCity Citizen</span>}
            {u.worlds?.map((w) => <span key={w} className="text-xs surface-2 rounded-full px-2.5 py-1 flex items-center gap-1"><MapPin size={11} />{w}</span>)}
            {[u.roles?.primary, u.roles?.secondary].filter(Boolean).map((r) => <span key={r} className="text-xs rounded-full px-2.5 py-1 bg-eblue/10 text-eblue border border-eblue/30">{r}</span>)}
          </div>
          {u.award_wins?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="profile-award-wins">
              {u.award_wins.slice().sort((a, b) => b.year - a.year).map((a, i) => (
                <button key={i} onClick={() => setWinCard({ title: `${a.category} Winner`, subtitle: `VirtuAwards ${a.year}` })} className="text-xs rounded-full px-2.5 py-1 bg-gold/15 text-gold border border-gold/40 flex items-center gap-1 font-medium hover:bg-gold/25 transition-colors" title={`Share your VirtuAwards ${a.year} win`} data-testid={`award-win-badge-${i}`}>
                  <Trophy size={11} /> {a.year} {a.category} Winner
                </button>
              ))}
            </div>
          )}
          {u.external_links?.length > 0 && <div className="flex flex-wrap gap-2 mt-2">{u.external_links.map((l, i) => <a key={i} href={l.url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-eblue flex items-center gap-1"><Link2 size={11} />{l.world || "link"}</a>)}</div>}
          {crews.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="profile-crew-badges">
              {primaryCrew && (
                <Link to={`/crew/@${primaryCrew.crew.handle}`} className="flex items-center gap-1.5 text-xs glass glass-gold rounded-full pl-1.5 pr-3 py-1 text-gold hover:brightness-125 transition" data-testid={`crew-badge-${primaryCrew.crew.handle}`}>
                  {primaryCrew.crew.emblem_url ? <img src={mediaUrl(primaryCrew.crew.emblem_url)} className="w-5 h-5 rounded-full object-cover" alt="" /> : <Star size={13} />}
                  <span className="font-semibold">{primaryCrew.crew.abbreviation || primaryCrew.crew.name}</span>
                  <span className="text-[10px] uppercase opacity-70">{primaryCrew.role}</span>
                </Link>
              )}
              {crews.length > 1 && <Link to={`/${u.username}?crews=1`} className="text-xs surface-2 rounded-full px-2.5 py-1 text-muted-foreground hover:text-white" data-testid="crew-badge-more">+{crews.length - 1} more</Link>}
            </div>
          )}
        </div>

        <CitizenCard city={u.city} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <Stat label="Followers" value={formatNum(u.follower_count)} />
          <Stat label="Following" value={formatNum(u.following_count)} />
          <Stat label="VStar Score" value={<VScore value={u.vstar_score} />} />
          <Stat label="Streams" value={formatNum(u.stream_count || 0)} />
        </div>

        {u.achievements?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {u.achievements.map((a, i) => { const Icon = ICONS[a.icon] || Trophy; return <span key={i} className="flex items-center gap-1.5 text-xs glass glass-gold rounded-full px-3 py-1.5 text-gold"><Icon size={13} />{a.label}</span>; })}
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          {(() => {
            const showShop = isSelf || u.has_products;
            const showBiz = isSelf || u.has_business;
            const base = isSelf ? ["dashboard", "posts", "reels", "music", "tv"] : ["posts", "reels", "music", "tv"];
            const tabsArr = [...base, ...(showShop ? ["shop"] : []), ...(showBiz ? ["business"] : []), "about"];
            const label = (t) => t === "dashboard" ? "Dashboard" : t === "tv" ? "TV" : t.charAt(0).toUpperCase() + t.slice(1);
            return (<>
              {/* Mobile: dropdown so tabs never overflow */}
              <div className="md:hidden relative">
                <button onClick={() => setTabMenuOpen((o) => !o)} data-testid="profile-tab-menu-btn"
                  className="w-full flex items-center justify-between surface-2 border border-white/10 rounded-xl px-4 h-11 text-sm font-medium">
                  <span className="flex items-center gap-2">{tab === "dashboard" && <LayoutDashboard size={15} className="text-eblue" />}{label(tab)}</span>
                  <ChevronDown size={16} className={`text-muted-foreground transition-transform ${tabMenuOpen ? "rotate-180" : ""}`} />
                </button>
                {tabMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setTabMenuOpen(false)} />
                    <div className="absolute z-40 mt-1.5 left-0 right-0 surface border border-white/10 rounded-xl overflow-hidden shadow-xl" data-testid="profile-tab-menu">
                      {tabsArr.map((t) => (
                        <button key={t} onClick={() => { setTab(t); setTabMenuOpen(false); }} data-testid={`profile-tab-option-${t}`}
                          className={`w-full text-left px-4 py-3 text-sm flex items-center gap-2 border-b border-white/5 last:border-0 ${tab === t ? "text-eblue bg-eblue/10" : "hover:bg-white/5"}`}>
                          {t === "dashboard" && <LayoutDashboard size={15} />}{label(t)}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              {/* Desktop: inline tab bar */}
              <TabsList className="hidden md:flex bg-transparent border-b border-white/8 w-full justify-start rounded-none h-auto p-0 gap-1">
                {tabsArr.map((t) => <TabsTrigger key={t} value={t} data-testid={`profile-tab-${t}`} className="capitalize rounded-none border-b-2 border-transparent data-[state=active]:border-eblue data-[state=active]:text-eblue data-[state=active]:bg-transparent px-4 pb-3">{t === "dashboard" ? <span className="flex items-center gap-1.5"><LayoutDashboard size={14} />Dashboard</span> : t === "tv" ? "TV" : t}</TabsTrigger>)}
              </TabsList>
            </>);
          })()}

          <TabsContent value="shop" className="pt-5">
            {!content.shop ? <Loading /> : (content.shop.products || []).length === 0 ? (
              <Empty title={isSelf ? "No products in your store yet" : "No products yet"} hint={isSelf ? "Open the Seller Dashboard to list digital products." : "This creator hasn't listed any products."} />
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm text-muted-foreground">{content.shop.product_count} products · {formatNum(content.shop.total_sold || 0)} sold</div>
                  <Link to={`/${u.username}/store`} className="text-sm text-eblue hover:underline" data-testid="profile-shop-viewall">Open full storefront →</Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {content.shop.products.map((p) => <ProductCard key={p.id} p={p} onOpen={setShopDetail} testid={`profile-shop-product-${p.id}`} />)}
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="business" className="pt-5">
            <ProfileBusiness userId={u.id} isSelf={isSelf} isPlus={me?.plan === "plus" || me?.role === "admin"} />
          </TabsContent>

          {isSelf && (
            <TabsContent value="dashboard" className="pt-5 max-w-3xl">
              <ProfileDashboard user={u} onShareStory={() => setStoryOpen(true)} />
            </TabsContent>
          )}
          <TabsContent value="posts" className="pt-5 max-w-[640px]">
            {!content.posts ? <Loading /> : content.posts.length === 0 ? <Empty title="No posts yet" /> : <div className="space-y-4">{content.posts.map((p) => <PostCard key={p.id} post={p} />)}</div>}
          </TabsContent>
          <TabsContent value="reels" className="pt-5">
            {!content.reels ? <Loading /> : content.reels.length === 0 ? <Empty title="No reels yet" /> : <div className="grid grid-cols-2 md:grid-cols-3 gap-3">{content.reels.map((r) => <video key={r.id} src={mediaUrl(r.video_url)} className="w-full aspect-[9/16] object-cover rounded-xl" muted />)}</div>}
          </TabsContent>
          <TabsContent value="music" className="pt-5">
            <ProfileMusic u={u} isSelf={isSelf} tracks={content.music} onPlay={player.playTrack} />
          </TabsContent>
          <TabsContent value="tv" className="pt-5">
            {!content.tv ? <Loading /> : content.tv.length === 0 ? <Empty title="No shows yet" /> : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{content.tv.map((v) => <VideoCard key={v.id} v={v} />)}</div>}
          </TabsContent>
          <TabsContent value="about" className="pt-5">
            <div className="surface rounded-2xl p-5 max-w-xl space-y-3 text-sm">
              <div><div className="text-xs text-muted-foreground">Bio</div><p>{u.bio || "No bio."}</p></div>
              <div><div className="text-xs text-muted-foreground">Industry roles</div><p>{[u.roles?.primary, u.roles?.secondary].filter(Boolean).join(", ") || "—"}</p></div>
              <div><div className="text-xs text-muted-foreground">Virtual worlds</div><p>{u.worlds?.join(", ") || "—"}</p></div>
              <div><div className="text-xs text-muted-foreground">Member since</div><p>{new Date(u.created_at).toLocaleDateString()}</p></div>
            </div>
            {(() => {
              const rl = u.real_life || {};
              if (!rl.text && !rl.image_url && !rl.video_url) return null;
              return (
                <div className="surface rounded-2xl p-5 max-w-xl mt-4 space-y-3" data-testid="profile-real-life">
                  <div className="text-sm font-display font-bold flex items-center gap-2"><Sparkles size={15} className="text-gold" />Real Life</div>
                  {rl.text && <p className="text-sm whitespace-pre-wrap text-silver">{rl.text}</p>}
                  {rl.image_url && <img src={mediaUrl(rl.image_url)} alt="" className="rounded-xl w-full object-cover max-h-80" />}
                  {rl.video_url && <video src={mediaUrl(rl.video_url)} controls playsInline className="rounded-xl w-full max-h-96 bg-black" />}
                </div>
              );
            })()}
          </TabsContent>
        </Tabs>
      </div>
      <TipModal open={tipOpen} onClose={() => setTipOpen(false)} recipient={u} refType="profile" refId={u.id} />
      {winCard && <ShareWinModal username={u.username} title={winCard.title} subtitle={winCard.subtitle} tier={winCard.tier} onClose={() => setWinCard(null)} />}
      {storyOpen && <StoryShareModal username={u.username} options={storyOptions} qr link={`${PUBLIC_WEB_URL}/${u.username}${isSelf && u.plan === "plus" ? `?ref=${u.username}` : ""}`} cacheKey={`${u.city ? `c${u.city.level}_${u.city.fame}_` : ""}${u.follower_count || 0}_${Math.round(u.vstar_score || 0)}`} onClose={() => setStoryOpen(false)} />}
      {shopDetail && <ProductDetail pid={shopDetail.id} onClose={() => setShopDetail(null)} onChange={() => api.get(`/marketplace/store/${u.username}`).then(({ data }) => setContent((c) => ({ ...c, shop: data })))} />}
      <ReportImpersonationModal open={reportOpen} onClose={() => setReportOpen(false)} targetType="user" targetId={u.id} targetLabel={`@${u.username}`} />
    </div>
  );
}



function Stat({ label, value }) {
  return <div className="surface rounded-xl p-3 text-center"><div className="font-display font-bold text-lg">{value}</div><div className="text-xs text-muted-foreground">{label}</div></div>;
}
